
import React, { useState } from 'react';
import { Button } from '@/components/ui/button';
import { Settings, Eye, EyeOff, RotateCcw } from 'lucide-react';
import {
  DropdownMenu,
  DropdownMenuTrigger,
  DropdownMenuContent,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuItem,
} from '@/components/ui/dropdown-menu';
import { useTheme } from '@/contexts/ThemeContext';
import { Badge } from '@/components/ui/badge';
import { Checkbox } from '@/components/ui/checkbox';
import { cn } from '@/lib/utils';

export interface ColumnDefinition {
  id: string;
  header: string;
}

interface TableColumnManagerProps {
  columns: ColumnDefinition[];
  columnVisibility: Record<string, boolean>;
  onColumnVisibilityChange: (visibility: Record<string, boolean>) => void;
  onResetColumns: () => void;
}

export function TableColumnManager({
  columns,
  columnVisibility,
  onColumnVisibilityChange,
  onResetColumns,
}: TableColumnManagerProps) {
  const [open, setOpen] = useState(false);
  const { theme } = useTheme();

  const handleToggleColumn = (columnId: string) => {
    const updatedVisibility = {
      ...columnVisibility,
      [columnId]: !columnVisibility[columnId],
    };
    onColumnVisibilityChange(updatedVisibility);
  };

  const visibleColumnsCount = columns.filter(col => columnVisibility[col.id] !== false).length;
  const totalColumnsCount = columns.length;

  const handleShowAll = () => {
    const allVisible = columns.reduce((acc, column) => {
      acc[column.id] = true;
      return acc;
    }, {} as Record<string, boolean>);
    onColumnVisibilityChange(allVisible);
  };

  const handleHideAll = () => {
    const allHidden = columns.reduce((acc, column) => {
      acc[column.id] = false;
      return acc;
    }, {} as Record<string, boolean>);
    onColumnVisibilityChange(allHidden);
  };

  return (
    <DropdownMenu open={open} onOpenChange={setOpen}>
      <DropdownMenuTrigger asChild>
        <Button 
          variant="outline" 
          size="sm" 
          className="h-8 flex gap-2 transition-colors"
        >
          <Settings className="h-3.5 w-3.5" />
          <span className="hidden sm:inline font-medium">Columns</span>
          <Badge 
            variant="secondary" 
            className="ml-1 px-2 py-0.5 text-xs font-medium"
          >
            {visibleColumnsCount}/{totalColumnsCount}
          </Badge>
        </Button>
      </DropdownMenuTrigger>
      
      <DropdownMenuContent 
        align="end" 
        className="min-w-[280px] max-h-[400px] overflow-hidden"
        sideOffset={8}
      >
        <div className="px-4 py-3 border-b">
          <DropdownMenuLabel className="text-sm font-semibold mb-0">
            Manage Table Columns
          </DropdownMenuLabel>
          <p className="text-xs text-muted-foreground mt-1">
            Show or hide columns to customize your view
          </p>
        </div>
        
        <div className="px-3 py-3 flex gap-2 border-b bg-muted/30">
          <Button
            variant="outline"
            size="sm"
            onClick={handleShowAll}
            className="flex-1 h-8 text-xs"
          >
            <Eye className="h-3 w-3 mr-1.5" />
            Show All
          </Button>
          <Button
            variant="outline"
            size="sm"
            onClick={handleHideAll}
            className="flex-1 h-8 text-xs"
          >
            <EyeOff className="h-3 w-3 mr-1.5" />
            Hide All
          </Button>
        </div>
        
        <div className="max-h-[300px] overflow-y-auto">
          {columns.map((column) => {
            const isVisible = columnVisibility[column.id] !== false;
            return (
              <div
                key={column.id}
                className={cn(
                  "flex items-center gap-3 px-4 py-2.5 cursor-pointer transition-colors",
                  "hover:bg-gray-50 dark:hover:bg-gray-800/50",
                  "focus:bg-gray-50 dark:focus:bg-gray-800/50"
                )}
                onClick={() => handleToggleColumn(column.id)}
              >
                <Checkbox
                  checked={isVisible}
                  onCheckedChange={() => handleToggleColumn(column.id)}
                  className="pointer-events-none"
                />
                <span className="font-medium text-sm flex-1">
                  {column.header || column.id}
                </span>
              </div>
            );
          })}
        </div>
        
        <DropdownMenuSeparator />
        <div className="p-2">
          <DropdownMenuItem 
            onClick={onResetColumns}
            className="text-sm cursor-pointer px-3 py-2 text-orange-600 hover:text-orange-700 hover:bg-orange-50 dark:text-orange-400 dark:hover:bg-orange-900/20"
          >
            <RotateCcw className="h-3.5 w-3.5 mr-2" />
            Reset to Default
          </DropdownMenuItem>
        </div>
      </DropdownMenuContent>
    </DropdownMenu>
  );
}
